import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {
  ChevronRight,
  FileUp,
  MoreHorizontal,
  Plus,
  Repeat
} from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { toast } from "sonner";

import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  duplicateStep,
  moveStep,
  removeNode,
  stepsOf,
  updateNode
} from "@/features/requestsIde/editor/stepModel";
import { cn } from "@/lib/utils";

import type { BuilderSelection } from "../hooks/useBuilderSelection";
import {
  addSection,
  addToSection,
  builderSections,
  issuesByNode,
  moveSection,
  moveStepBy,
  removeSection,
  renameSection,
  type BuilderSection
} from "../model";
import { QuestionRow } from "./QuestionRow";

export type QuestionsCardProps = {
  schema: OutgoingIntakeFormNode[];
  onChange: (schema: OutgoingIntakeFormNode[]) => void;
  selection: BuilderSelection;
  /** "Mariana", or "The client" on a template. */
  who: string;
  /** Sent intakes and starters: no grips, adds or issue lines. */
  readOnly?: boolean;
  /** Steps Casey just changed. */
  freshIds?: ReadonlySet<string>;
  /** The "From Library" control at the end of a section's add row. */
  renderLibrary?: (section: BuilderSection) => ReactNode;
  /** Shown inside the card when there are no questions yet. */
  emptyHint?: ReactNode;
};

const SectionTitle: FC<{
  title: string;
  onCommit: (title: string) => void;
  onCancel: () => void;
}> = ({ title, onCommit, onCancel }) => {
  const [value, setValue] = useState(title);
  return (
    <Input
      autoFocus
      value={value}
      aria-label="Section Title"
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => (value.trim() ? onCommit(value.trim()) : onCancel())}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (value.trim()) onCommit(value.trim());
          else onCancel();
        } else if (e.key === "Escape") onCancel();
      }}
      className="h-6 max-w-64 text-base font-medium"
    />
  );
};

/**
 * The intake's questions as one card: a header per section, each step a row
 * that opens in place, and an add row under every section. Shared by the
 * Requests builder and the Firm Knowledge template editor.
 */
export const QuestionsCard: FC<QuestionsCardProps> = ({
  schema,
  onChange,
  selection,
  who,
  readOnly = false,
  freshIds,
  renderLibrary,
  emptyHint
}) => {
  const [renaming, setRenaming] = useState<string | null>(null);
  const sections = builderSections(schema);
  const ids = sections.flatMap((s) => s.steps.map((st) => st.node.node_id));
  const issues = readOnly ? new Map<string, string>() : issuesByNode(schema);
  const { openId, setOpenId, collapsed, toggleSection } = selection;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const add = (next: { schema: OutgoingIntakeFormNode[]; nodeId: string }) => {
    onChange(next.schema);
    setOpenId(next.nodeId);
  };

  const remove = (nodeId: string, label: string) => {
    const before = schema;
    onChange(removeNode(schema, nodeId));
    if (openId === nodeId) setOpenId(null);
    toast(`Deleted “${label}”`, {
      action: { label: "Undo", onClick: () => onChange(before) }
    });
  };

  const removeWholeSection = (section: BuilderSection) => {
    if (!section.sectionId) return;
    const before = schema;
    onChange(removeSection(schema, section.sectionId));
    toast(
      `Deleted “${section.title}” and its ${section.steps.length} questions`,
      {
        action: { label: "Undo", onClick: () => onChange(before) }
      }
    );
  };

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const after = from < to ? String(over.id) : ids[to - 1] ?? null;
    onChange(moveStep(schema, String(active.id), after));
  };

  const named = sections.filter((s) => s.sectionId);
  const topIds = schema.map((n) => n.node_id);

  return (
    <div className="overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2">
      {sections.length === 0 ? (
        <div className="flex flex-col items-center gap-1 px-6 py-10 text-center">
          <span className="text-base text-fg">No questions yet</span>
          <span className="text-sm text-fg-3">
            {emptyHint ?? "Add a section to start, or ask Casey to draft it."}
          </span>
        </div>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {sections.map((section, si) => {
            const shut = collapsed.has(section.id);
            const top = section.sectionId
              ? topIds.indexOf(section.sectionId)
              : -1;
            return (
              <div
                key={section.id}
                data-builder-section={section.id}
                className={cn(si > 0 && "border-t border-line-2")}
              >
                <div className="flex h-10 items-center gap-2 bg-fill-1 pr-2.5 pl-3">
                  <button
                    type="button"
                    aria-label={shut ? "Expand Section" : "Collapse Section"}
                    aria-expanded={!shut}
                    onClick={() => toggleSection(section.id)}
                    className="inline-flex text-fg-3 hover:text-fg"
                  >
                    <ChevronRight
                      className={cn(
                        "size-3.5 transition-transform",
                        !shut && "rotate-90"
                      )}
                    />
                  </button>
                  {renaming === section.id && section.sectionId ? (
                    <SectionTitle
                      title={section.title}
                      onCommit={(title) => {
                        onChange(
                          renameSection(
                            schema,
                            section.sectionId as string,
                            title
                          )
                        );
                        setRenaming(null);
                      }}
                      onCancel={() => setRenaming(null)}
                    />
                  ) : (
                    <button
                      type="button"
                      className="min-w-0 truncate text-base font-medium text-fg"
                      onClick={() => toggleSection(section.id)}
                      onDoubleClick={() =>
                        !readOnly && section.sectionId
                          ? setRenaming(section.id)
                          : undefined
                      }
                    >
                      {section.title}
                    </button>
                  )}
                  <span className="shrink-0 text-sm text-fg-3">
                    {section.steps.length} question
                    {section.steps.length === 1 ? "" : "s"}
                  </span>
                  <span className="flex-1" />
                  {readOnly || !section.sectionId ? null : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`${section.title} Options`}
                        >
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem
                          onSelect={() => setRenaming(section.id)}
                        >
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={top <= 0}
                          onSelect={() =>
                            onChange(
                              moveSection(
                                schema,
                                section.sectionId as string,
                                -1
                              )
                            )
                          }
                        >
                          Move Up
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={top < 0 || top >= topIds.length - 1}
                          onSelect={() =>
                            onChange(
                              moveSection(
                                schema,
                                section.sectionId as string,
                                1
                              )
                            )
                          }
                        >
                          Move Down
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => removeWholeSection(section)}
                        >
                          Delete Section
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                {shut
                  ? null
                  : section.steps.map((step) => {
                      const { node } = step;
                      const index = ids.indexOf(node.node_id);
                      return (
                        <QuestionRow
                          key={node.node_id}
                          step={step}
                          schema={schema}
                          who={who}
                          open={openId === node.node_id}
                          readOnly={readOnly}
                          issue={issues.get(node.node_id) ?? null}
                          fresh={freshIds?.has(node.node_id)}
                          isFirst={index === 0}
                          isLast={index === ids.length - 1}
                          onToggle={() =>
                            setOpenId(
                              openId === node.node_id ? null : node.node_id
                            )
                          }
                          onChange={(patch) =>
                            onChange(updateNode(schema, node.node_id, patch))
                          }
                          onDuplicate={() => {
                            const next = duplicateStep(schema, node.node_id);
                            const after = stepsOf(next).map(
                              (s) => s.node.node_id
                            );
                            onChange(next);
                            setOpenId(after[after.indexOf(node.node_id) + 1]);
                          }}
                          onMove={(delta) =>
                            onChange(moveStepBy(schema, node.node_id, delta))
                          }
                          onDelete={() =>
                            remove(
                              node.node_id,
                              String(node.prompt ?? node.title ?? "question")
                            )
                          }
                        />
                      );
                    })}
                {shut || readOnly ? null : (
                  <div className="flex flex-wrap gap-0.5 border-t border-line-1 py-1.5 pr-3.5 pl-14.5">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() =>
                        add(addToSection(schema, section, "question"))
                      }
                    >
                      <Plus className="size-3" /> Question
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() =>
                        add(addToSection(schema, section, "upload"))
                      }
                    >
                      <FileUp className="size-3" /> Upload
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => add(addToSection(schema, section, "list"))}
                    >
                      <Repeat className="size-3" /> List
                    </Button>
                    {renderLibrary?.(section)}
                  </div>
                )}
              </div>
            );
          })}
        </SortableContext>
      </DndContext>
      {readOnly ? null : (
        <div
          className={cn(
            "flex gap-0.5 bg-fill-1 px-3 py-2",
            sections.length > 0 && "border-t border-line-2"
          )}
        >
          <Button
            variant="ghost"
            size="xs"
            onClick={() =>
              add(
                addSection(
                  schema,
                  named.length || sections.length === 0
                    ? "New Section"
                    : "More Questions"
                )
              )
            }
          >
            <Plus className="size-3" /> Add Section
          </Button>
        </div>
      )}
    </div>
  );
};
